import List from "@mui/material/List";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ListSubheader from "@mui/material/ListSubheader";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { useNavigate, useParams } from "react-router";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DisplayDate from "../../../../Component/DisplayDate/DisplayDate";
import { TaskmapHistoryEntry } from "../../../../API/taskmapConfigHistory";
import styles from "../field/components/ListItem.module.css";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import { mergeTaskmaps } from "../../../../API/taskmap";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import {
  formatBlossomRange,
  getActiveZones,
} from "../taskmap/taskmapFormatters";
import { translateProductName } from "../../../../Component/Product/ProductName";
import TaskmapPopup from "../taskmap/TaskmapPopup";
import { getFileNameFromHeader } from "../taskmap/utils/file-utils";
import { DateTime } from "luxon";

export type FieldData = {
  id: number;
  name: string;
  taskmaps: TaskmapHistoryEntry[];
};

type ProductGroup = "BLOSSOM" | "VIGOUR";

const PRODUCT_GROUPS: ProductGroup[] = ["BLOSSOM", "VIGOUR"];
const PRODUCT_GROUP_SKUS: Record<ProductGroup, string[]> = {
  VIGOUR: ["OR1", "TS-VIGOUR"],
  BLOSSOM: ["OR2", "TS-BLOSSOM"],
};
const PRODUCT_GROUP_REPRESENTATIVE_SKU: Record<ProductGroup, string> = {
  BLOSSOM: "TS-BLOSSOM",
  VIGOUR: "TS-VIGOUR",
};

function productGroup(sku: string): ProductGroup | null {
  for (const group of PRODUCT_GROUPS) {
    if (PRODUCT_GROUP_SKUS[group].includes(sku)) return group;
  }
  return null;
}

function machineType(entry: TaskmapHistoryEntry): string | undefined {
  const machine = entry.generation_parameters.parameters.machine_parameters as
    | { type?: string }
    | undefined;
  return machine?.type;
}

function sameGenerationParameters(taskmaps: TaskmapHistoryEntry[]): boolean {
  if (taskmaps.length < 2) return true;
  const [first, ...rest] = taskmaps;
  const firstTerminal = first.generation_parameters.terminal?.type;
  const firstMachine = machineType(first);
  return rest.every(
    (tm) =>
      tm.generation_parameters.terminal?.type === firstTerminal &&
      machineType(tm) === firstMachine,
  );
}

type MergeTaskmapsProps = {
  onMergeTaskmaps: () => void;
  taskmapsByFieldId: Record<number, FieldData>;
};

export function MergeTaskmaps({
  taskmapsByFieldId,
  onMergeTaskmaps,
}: MergeTaskmapsProps) {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [selectedProduct, setSelectedProduct] = useState<ProductGroup>(
    () => PRODUCT_GROUPS[0],
  );

  const filteredFieldData = useMemo(() => {
    const result: Record<number, FieldData> = {};
    Object.values(taskmapsByFieldId).forEach((field) => {
      result[field.id] = {
        ...field,
        taskmaps: field.taskmaps.filter(
          (tm) => productGroup(tm.product_sku) === selectedProduct,
        ),
      };
    });
    return result;
  }, [taskmapsByFieldId, selectedProduct]);

  const [selectedTaskmaps, setSelectedTaskmaps] = useState<
    Record<number, TaskmapHistoryEntry>
  >({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [mergedTaskmap, setMergedTaskmap] = useState<File | null>(null);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);

  useEffect(() => {
    const initialSelections: Record<number, TaskmapHistoryEntry> = {};
    Object.values(filteredFieldData).forEach((field) => {
      if (field.taskmaps[0]) {
        initialSelections[field.id] = field.taskmaps[0];
      }
    });
    setSelectedTaskmaps(initialSelections);
  }, [filteredFieldData]);

  const handleSelectionUpdate = useCallback(
    (fieldId: number, selectedTaskmap: TaskmapHistoryEntry) => {
      setSelectedTaskmaps((prev) => ({
        ...prev,
        [fieldId]: selectedTaskmap,
      }));
    },
    [],
  );

  const onSubmit = async () => {
    if (!customerId) return;
    const taskmaps = Object.values(selectedTaskmaps).filter(
      (taskmap) => taskmap,
    );
    if (taskmaps.length < 2) {
      setError(t("error.merge_taskmaps.select_at_least_two"));
      return;
    }
    if (!sameGenerationParameters(taskmaps)) {
      setError(t("error.merge_taskmaps.parameter_mismatch"));
      return;
    }

    setLoading(true);
    setError(null);
    const response = await mergeTaskmaps(
      customerId,
      taskmaps.map((taskmap) => taskmap.id),
    );

    setLoading(false);

    if (!response || !response.ok) {
      setError(t("text.error_occured"));
      return;
    }
    setMergedTaskmap(
      new File([await response.blob()], getFileNameFromHeader(response)),
    );
    setPopupOpen(true);
  };

  return (
    <>
      <List className={styles.list}>
        <ListSubheader component="li" className={styles.listSubheader}>
          {t("heading.taskmaps.merge")}
        </ListSubheader>
        <Box
          component="li"
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: "var(--color-secondary-mint)",
            borderBottom: "solid 1px rgba(0, 0, 0, 0.12)",
          }}
        >
          <ToggleButtonGroup
            fullWidth
            size="small"
            color="primary"
            value={selectedProduct}
            exclusive
            onChange={(_, value: ProductGroup | null) => {
              if (value) setSelectedProduct(value);
            }}
            aria-label={t("label.product") as string}
            data-testid="select-merge-product"
            sx={{ backgroundColor: "var(--boxed-background)" }}
          >
            {PRODUCT_GROUPS.map((group) => (
              <ToggleButton key={group} value={group}>
                {translateProductName(
                  t,
                  PRODUCT_GROUP_REPRESENTATIVE_SKU[group],
                )}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {Object.values(filteredFieldData).map((fieldData) => {
          const fieldId = fieldData.id;
          const selectedTaskmap = selectedTaskmaps[fieldId];
          return (
            <ListItem key={fieldId} className={styles.listItem}>
              <div className={styles.listItemBox}>
                <div className="vspace">
                  <ListItemText
                    primary={fieldData.name}
                    primaryTypographyProps={{ fontWeight: "bold" }}
                  />
                </div>
                {fieldData.taskmaps.length > 0 && selectedTaskmap ? (
                  <SelectTaskmap
                    fieldId={fieldId}
                    taskmaps={fieldData.taskmaps}
                    selectedTaskmap={selectedTaskmap}
                    onSelectTaskmap={(taskmap) =>
                      handleSelectionUpdate(fieldId, taskmap)
                    }
                  />
                ) : (
                  <>
                    <div className="vspace">
                      <p className={styles.listItemText}>
                        {t("message.no_taskmaps")}
                      </p>
                    </div>
                    <div className="vspace">
                      <Button
                        variant="contained"
                        disableElevation
                        fullWidth
                        size="large"
                        onClick={() =>
                          navigate(`/customer/${customerId}/field/${fieldId}`)
                        }
                      >
                        {t("button.Create taskmap")}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </ListItem>
          );
        })}
        <ListItem className="vspace" sx={{ display: "block" }}>
          <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
            <Button
              variant="contained"
              disableElevation
              fullWidth
              size="large"
              onClick={onSubmit}
              loading={loading}
            >
              {t("button.merge_taskmaps")}
            </Button>
            {error && (
              <Alert
                severity="error"
                variant="outlined"
                onClose={() => setError(null)}
                sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {error}
              </Alert>
            )}
          </Stack>
        </ListItem>
      </List>
      <TaskmapPopup
        taskmap={mergedTaskmap}
        open={popupOpen}
        setOpen={(open) => {
          setPopupOpen(open);
          if (!open) {
            setMergedTaskmap(null);
            onMergeTaskmaps();
          }
        }}
      />
    </>
  );
}

type SelectTaskmapProps = {
  fieldId: number;
  taskmaps: TaskmapHistoryEntry[];
  onSelectTaskmap: (taskmap: TaskmapHistoryEntry) => void;
  selectedTaskmap: TaskmapHistoryEntry;
};

function SelectTaskmap({
  fieldId,
  taskmaps,
  selectedTaskmap,
  onSelectTaskmap,
}: SelectTaskmapProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <div className="vspace">
      <Select
        key={fieldId}
        fullWidth
        value={selectedTaskmap.id}
        onChange={(event) => {
          const selected = taskmaps.find(
            (taskmap) => taskmap.id === event.target.value,
          );
          if (selected) {
            onSelectTaskmap(selected);
          }
        }}
        data-testid="select-taskmap-id"
        renderValue={(value) => {
          const taskmap = taskmaps.find((tm) => tm.id === value);
          if (!taskmap) return null;
          return (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              <DisplayDate
                date={taskmap.created_at}
                format={DateTime.DATETIME_MED}
              />
            </Typography>
          );
        }}
      >
        {taskmaps.map((taskmap: TaskmapHistoryEntry) => {
          const terminalType = taskmap.generation_parameters.terminal?.type;
          const machine = machineType(taskmap);
          return (
            <MenuItem value={taskmap.id} key={taskmap.id}>
              <Stack spacing={0.25} sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <DisplayDate
                    date={taskmap.created_at}
                    format={DateTime.DATETIME_MED}
                  />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {`${t("label.Machine")}:`}
                  </Box>{" "}
                  {t(`machine.${machine}`)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {`${t("label.Terminal")}:`}
                  </Box>{" "}
                  {t(`terminal.${terminalType}`)}
                </Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText
        component="div"
        sx={{ fontSize: "0.85rem", mt: 0.5, mx: 0 }}
      >
        {productGroup(selectedTaskmap.product_sku) === "VIGOUR" ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", whiteSpace: "normal", lineHeight: 1.4 }}
          >
            {getActiveZones(selectedTaskmap)
              .map(
                (zone) =>
                  `${t("taskmap.label.class", { n: zone.lower })}: ${zone.rate}%`,
              )
              .join(", ") || "N/A"}
          </Typography>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", whiteSpace: "normal", lineHeight: 1.4 }}
          >
            {t("label.blossom_range")}
            {": "}
            {formatBlossomRange(selectedTaskmap)}
          </Typography>
        )}
      </FormHelperText>
    </div>
  );
}
